import React from "react";
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {IconArrowRight, IconPencil} from "@tabler/icons-react";
import {UI, rgba} from "../../uiTokens";
import {iconBtnSx} from "../editMenuStyles";

export const SCOPE_META = {
    const: {label: "const", color: "#7C3AED"},
    flow: {label: "flow", color: UI.accent},
    global: {label: "global", color: "#0E9F9A"},
    form: {label: "form", color: "#D97706"},
};

const ScopeTag = ({scope}) => {
    const meta = SCOPE_META[scope] || {label: scope || "—", color: UI.muted};
    return (
        <Box
            sx={{
                flexShrink: 0,
                px: 0.6,
                borderRadius: "5px",
                fontSize: 9,
                fontWeight: 700,
                lineHeight: "16px",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: meta.color,
                backgroundColor: rgba(meta.color, 0.12),
            }}
        >
            {meta.label}
        </Box>
    );
};

const ValueLine = ({label, scope, value}) => (
    <Stack direction="row" gap={0.75} alignItems="center" sx={{minWidth: 0}}>
        <Typography sx={{width: 26, flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: UI.muted}}>
            {label}
        </Typography>
        <ScopeTag scope={scope}/>
        <Tooltip title={value || ""} disableInteractive>
            <Typography
                noWrap
                sx={{flex: 1, minWidth: 0, fontSize: 12, color: value ? UI.ink : rgba(UI.muted, 0.7),
                    fontFamily: "'Roboto Mono', ui-monospace, monospace"}}
            >
                {value || "—"}
            </Typography>
        </Tooltip>
    </Stack>
);

/**
 * One "set variable" assignment — shows where the value comes from and where it
 * is written, plus scope tags. Click anywhere (or the pencil) to edit.
 */
export const VarAssignmentCard = ({param, onEdit}) => {
    const isConst = param?.source_scope === "const";

    return (
        <Box
            onClick={onEdit}
            sx={{
                p: 1,
                borderRadius: "10px",
                border: `1px solid ${rgba(UI.ink, 0.1)}`,
                backgroundColor: UI.surface,
                cursor: "pointer",
                transition: "border-color .12s ease, background-color .12s ease",
                "&:hover": {borderColor: rgba(UI.accent, 0.4), backgroundColor: rgba(UI.accent, 0.03)},
                "&:hover .var-card-edit": {opacity: 1},
            }}
        >
            <Stack direction="row" alignItems="center" gap={0.75}>
                <Box
                    sx={{
                        px: 0.75,
                        borderRadius: "6px",
                        fontSize: 9,
                        fontWeight: 700,
                        lineHeight: "18px",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: isConst ? "#7C3AED" : UI.accent,
                        backgroundColor: rgba(isConst ? "#7C3AED" : UI.accent, 0.12),
                    }}
                >
                    {isConst ? "Constant" : "Transfer"}
                </Box>
                <Box sx={{flex: 1}}/>
                <IconButton
                    size="small"
                    className="var-card-edit"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit?.();
                    }}
                    sx={{...iconBtnSx, opacity: 0, transition: "opacity .12s ease"}}
                >
                    <IconPencil size={15}/>
                </IconButton>
            </Stack>

            <Stack gap={0.4} sx={{mt: 0.75}}>
                <ValueLine label="FROM" scope={isConst ? "const" : param?.source_scope} value={param?.source}/>
                <Stack direction="row" alignItems="center" gap={0.5} sx={{pl: "26px", color: rgba(UI.ink, 0.35)}}>
                    <IconArrowRight size={12}/>
                </Stack>
                <ValueLine label="TO" scope={param?.target_scope} value={param?.target}/>
            </Stack>
        </Box>
    );
};
